// cote3d.ts — layerul de COTE al scenei 3D.
//
// CE INTRĂ, și de ce atât. O clădire are sute de cote; un layer care le arată
// pe toate nu e un layer, e o planșă. Sunt trei familii, alese fiindcă
// răspund la întrebările pe care le pui uitându-te la un model 3D — «cât e de
// mare?», «cât e de înalt?», «unde e pământul față de ea?»:
//
//   1. LATURILE exterioare — două cote la bază, pe X și pe Y, pe conturul
//      amprentei. Din ele se citește gabaritul;
//   2. ÎNĂLȚIMILE DE ETAJ — un lanț vertical pe un colț, o cotă per etaj,
//      plus înălțimea totală de la ±0,00 la ultima placă, mai în afară;
//   3. COTELE DE NIVEL — ±0,00, terenul natural și cota de fundare, ca
//      marcaje pe aceeași verticală. Fără ele nu se vede cât e îngropată
//      clădirea.
//   Traveele, secțiunile elementelor și grosimile sunt în planurile și în
//   secțiunile pașilor lor — aici ar face zgomot, nu informație.
//
// Stilul e al planșei (`hud3d.ts`): săgeți pline, martori continui care trec
// peste linia de cotă, valoarea scrisă în lungul cotei, în planul ei, fără
// titluri — desenul spune singur ce e o lungime și ce e o înălțime. Cota de
// nivel are triunghiul de planșă, linia orizontală și cota scrisă deasupra,
// cu numele reperului.
//
// CULOAREA E A COTELOR DE PE PLANURI: linia în accentul temei,
// textul în cerneală pe o pastilă albă — aceeași cotă pe situație,
// arhitectură și structură. Cotele de nivel prea apropiate una de alta
// (±0,00, CTN, fundarea, la jumătate de metru) nu se mai scriu una peste
// alta: fiecare iese ceva mai în afară decât vecina de sub ea.

import * as THREE from "three";
import { PAL } from "./paletaDesen";
import { FemResult } from "./tipuri";
import {
  cotaLiniara, materialLinie, materialMartor, materialSageata, mFmt,
  segmente, textCota, triunghiuri,
} from "./hud3d";

type V3 = [number, number, number];

export interface CutieModel {
  xmin: number; xmax: number; ymin: number; ymax: number;
  zmin: number; zmax: number;
}

/** Cotele clădirii, ca grup 3D. `cutie` e gabaritul modelului (nodurile). */
export function grupCote(r: FemResult, cutie: CutieModel) {
  const grup = new THREE.Group();
  const model = r.model;
  const { xmin, xmax, ymin, ymax } = cutie;
  const lx = Math.max(xmax - xmin, 1);
  const ly = Math.max(ymax - ymin, 1);
  const scara = Math.max(lx, ly);
  // toate mărimile de desen sunt proporționale cu clădirea: aceeași planșă
  // trebuie să arate la fel pe o casă de 8 m și pe un bloc de 40
  const sageata = Math.max(0.22, scara * 0.018);
  // Depărtarea liniei de cotă față de clădire. La 6 % din gabarit, pe o casă
  // de 10 m linia stătea la 60 cm de perete — lipită de el, cu martorii
  // înghițiți de muchie. Zece la sută, și niciodată sub 1,2 m: cota trebuie
  // să stea în afara desenului ca să se citească drept cotă.
  const pas = Math.max(1.2, scara * 0.1);
  const text = Math.max(0.32, scara * 0.025);

  const linii: number[] = [];
  const martori: number[] = [];
  const sageti: number[] = [];
  const cota = (a: V3, b: V3, dep: V3, valoare: string,
                culoare = PAL.ink, ingrosat = false) => {
    const c = cotaLiniara(a, b, dep, sageata);
    linii.push(...c.linii);
    martori.push(...c.martori);
    sageti.push(...c.sageti);
    const t = textCota(valoare, text, c.mijloc, c.dir, c.nrm, culoare,
                       ingrosat, true);
    if (t) grup.add(t);
    return c;
  };

  // --- 1. LATURILE exterioare, la baza clădirii ----------------------------
  const z0 = cutie.zmin;
  cota([xmin, ymin, z0], [xmax, ymin, z0], [0, -pas, 0], `${mFmt(lx)} m`);
  cota([xmax, ymin, z0], [xmax, ymax, z0], [pas, 0, 0], `${mFmt(ly)} m`);

  // --- 2. ÎNĂLȚIMILE DE ETAJ, lanț vertical pe un colț ---------------------
  // colțul (xmin, ymin) e cel dinspre camera implicită; lanțul urcă în afara
  // clădirii, ca martorii să nu treacă prin ea
  const cx = xmin;
  const cy = ymin;
  const etaje = (model.etaje ?? [])
    .slice()
    .sort((a, b) => a.z_m - b.z_m);
  const cote_z: number[] = [0];
  etaje.forEach((e) => {
    if (!cote_z.some((z) => Math.abs(z - e.z_m) < 1e-6)) cote_z.push(e.z_m);
  });
  cote_z.sort((a, b) => a - b);

  // deplasarea pe diagonala colțului: textul stă în planul vertical care
  // conține diagonala, deci se vede din direcția camerei implicite
  const depLant: V3 = [-pas * 0.7, -pas * 0.7, 0];
  for (let i = 1; i < cote_z.length; i++) {
    const jos = cote_z[i - 1];
    const sus = cote_z[i];
    cota([cx, cy, jos], [cx, cy, sus], depLant, mFmt(sus - jos));
  }
  // …și totalul, mai în afară, îngroșat: e cifra de pe fișa clădirii
  if (cote_z.length > 2) {
    const sus = cote_z[cote_z.length - 1];
    cota([cx, cy, cote_z[0]], [cx, cy, sus],
         [depLant[0] * 2.4, depLant[1] * 2.4, 0],
         `${mFmt(sus - cote_z[0])} m`, PAL.ink, true);
  }

  // --- 3. COTELE DE NIVEL: ±0,00, terenul, cota de fundare -----------------
  const sap = model.sapatura;
  const zCtn = sap?.teren?.z_ctn_m ?? sap?.h_pardoseala_sub_teren_m ?? null;
  const zFund = sap?.santuri?.length
    ? -Math.max(...sap.santuri.map((s) => s.Df_m)) : null;
  const niveluri: [number, string, string][] = [[0, "", PAL.ink]];
  if (zCtn != null && Math.abs(zCtn) > 1e-6)
    niveluri.push([zCtn, "CTN", PAL.mut]);
  if (zFund != null) niveluri.push([zFund, "FUNDARE", PAL.amb]);
  // direcția în care iese marcajul: diagonala colțului (xmax, ymax), în afară
  const iesire = new THREE.Vector3(1, 1, 0).normalize();
  const sus = new THREE.Vector3(0, 0, 1);
  // treapta fiecărei cote de nivel: una prea aproape (pe verticală) de cea de
  // dedesubt iese încă o dată mai în afară, ca textele să nu se calce
  niveluri.sort((p, q) => p[0] - q[0]);
  const trepte: number[] = [];
  niveluri.forEach(([z], i) => {
    const aproape = i > 0 && Math.abs(z - niveluri[i - 1][0]) < text * 2.2;
    trepte.push(aproape ? trepte[i - 1] + 1 : 0);
  });
  niveluri.forEach(([z, nume, culoare], i) => {
    // triunghiul de cotă de nivel, cu vârful pe cotă, apoi linia orizontală
    const a = new THREE.Vector3(xmax, ymax, z);
    const iesit = pas * 1.6 * (1 + trepte[i]);
    const b = a.clone().addScaledVector(iesire, iesit);
    linii.push(a.x, a.y, a.z, b.x, b.y, b.z);
    const t1 = a.clone().addScaledVector(iesire, -sageata * 0.5)
      .addScaledVector(sus, sageata);
    const t2 = a.clone().addScaledVector(iesire, sageata * 0.5)
      .addScaledVector(sus, sageata);
    linii.push(t1.x, t1.y, t1.z, a.x, a.y, a.z,
               a.x, a.y, a.z, t2.x, t2.y, t2.z,
               t1.x, t1.y, t1.z, t2.x, t2.y, t2.z);
    const semn = z > 1e-6 ? "+" : z < -1e-6 ? "−" : "±";
    const valoare = `${semn}${mFmt(Math.abs(z))}${nume ? ` ${nume}` : ""}`;
    const mijloc = a.clone().addScaledVector(iesire, iesit - pas * 0.7);
    const t = textCota(valoare, text, [mijloc.x, mijloc.y, mijloc.z],
                       iesire, sus, culoare, !nume, true);
    if (t) grup.add(t);
  });

  if (linii.length) grup.add(segmente(linii, materialLinie(PAL.acc)));
  if (sageti.length) grup.add(triunghiuri(sageti, materialSageata(PAL.acc)));
  if (martori.length)
    grup.add(segmente(martori, materialMartor(PAL.mut), true));
  return grup;
}
